import { type DesignUploadCookieData } from "./designUploadCookie";

/**
 * Map MIME types to proper file extensions
 */
const MIME_TO_EXTENSION: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/svg+xml": "svg",
  "image/bmp": "bmp",
  "image/tiff": "tiff",
  "image/x-icon": "ico",
  "application/pdf": "pdf"
};

/**
 * Get file extension from MIME type
 */
function getExtensionFromMimeType(mimeType: string): string {
  // First, check our known mappings
  if (MIME_TO_EXTENSION[mimeType]) {
    return MIME_TO_EXTENSION[mimeType];
  }

  // For unknown types, try to extract a reasonable extension
  const parts = mimeType.split("/");
  if (parts.length === 2 && parts[1]) {
    const subtype = parts[1];
    // Remove any structured syntax suffix (e.g., +xml, +json)
    const extensionParts = subtype.split("+");
    const extension = extensionParts[0] || subtype;
    // Remove any vendor prefix (e.g., vnd.ms-excel becomes excel)
    const cleanExtension = extension.replace(/^vnd\.[\w-]+\./, "").replace(/^x-/, "");
    return cleanExtension || "png";
  }

  // Default fallback
  return "png";
}

export async function downloadCookieArtwork(cookieArtwork: DesignUploadCookieData): Promise<File> {
  const imageUrl = cookieArtwork.url || cookieArtwork.original_image_url;
  const response = await fetch(imageUrl);

  if (!response.ok) {
    throw new Error(`Failed to download image: ${response.status}`);
  }

  const blob = await response.blob();

  // Validate blob size
  if (blob.size === 0) {
    throw new Error("Downloaded file is empty");
  }

  // Create a File object from the blob
  const mimeType = cookieArtwork.original_mime || blob.type || "image/png";
  const extension = getExtensionFromMimeType(mimeType);
  const fileName = `artwork-${cookieArtwork.id}.${extension}`;

  return new File([blob], fileName, { type: mimeType });
}
